import { observer } from 'src/utils/mobx-react';
import React, { useState } from 'react';
import { MarketName } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/MarketName';
import { useAppStateContext } from 'src/AppState/AppState';
import { MarketIdComponent } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/MarketIdComponent';
import { MarketBaseSettings } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketBaseSettings/MarketBaseSettings';
import { MarketLimits } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketLimits/MarketLimits';
import { MarketFeed } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketFeed/MarketFeed';
import { MarketCashOut } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketCashOut/MarketCashOut';
import { MarketEachWay } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketEachWay/MarketEachWay';
import { MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { MarketSettingsState } from './MarketSettings.state';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { DateTimeInput } from 'src/components/DateTimeInput/DateTimeInput';

interface DatePickerWrapperPropsType {
    state: MarketSettingsState;
}

const DatePickerWrapper = observer('DatePickerWrapper', ({ state }: DatePickerWrapperPropsType) => {
    const value = state.suspendTimeValue === null ? '' : state.suspendTimeValue.toISOString();

    const onChange = async (date: string | null): Promise<void> => {
        if (date === null) {
            await state.onSuspendTimeChange(null);
        } else {
            await state.onSuspendTimeChange(new Date(date));
        }
    };

    return (
        <DateTimeInput
            value={value}
            dateFormat='yyyy-MM-dd'
            timeFormat='HH:mm:ss'
            required={false}
            onChange={onChange}
        />
    );
});

export type MarketSettingsPropsType = {
    marketId: MarketId;
};

export const MarketSettings = observer('MarketSettings', ({ marketId }: MarketSettingsPropsType) => {
    const appState = useAppStateContext();

    const [stateMap] = useState(new AutoMap((marketId: MarketId) => new MarketSettingsState(appState, marketId)));

    const state = stateMap.get(marketId);
    const marketModel = state.marketModel;

    if (marketModel === null) {
        return null;
    }

    return (
        <div className='col-4'>
            <h4>Settings</h4>
            <div className='row'>
                <MarketName
                    dataTest='market-name'
                    label='Name'
                    name='name'
                    value={marketModel.name}
                    onSave={state.onMarketNameSave}
                />
            </div>
            <div className='row'>
                <div className='col-6 suspend-time'>
                    <div
                        className='element hasLabel'
                        data-test='suspend-time-date-picker'
                    >
                        <DatePickerWrapper state={state} />
                        <label>Suspend time</label>
                    </div>
                </div>

                <div className='col-6'>
                    <div
                        className='element checkbox'
                        data-test='auto-take-down-checkbox'
                    >
                        <label>
                            <input
                                type='checkbox'
                                checked={state.autoTakeDownValue}
                                disabled={marketModel.getSuspendTime.length === 0}
                                onChange={state.onChangeAutoTakeDown}
                            />
                            <span>Auto take down</span>
                        </label>
                    </div>
                </div>
            </div>

            <div className='row'>
                <MarketIdComponent marketId={marketId} />
            </div>
            <div className='row'>
                <MarketBaseSettings marketId={marketModel.id2} />
            </div>
            <MarketLimits marketId={marketModel.id2} />
            <MarketFeed marketId={marketModel.id2} />
            <MarketCashOut marketId={marketModel.id2} />
            {marketModel.getData().tricastsOffered !== undefined && marketModel.getData().tricastsOffered !== null ? (
                <div>
                    <h4>Tricasts / Forecasts</h4>

                    <div className='row'>
                        <div className='col-6'>
                            <div
                                className='element checkbox'
                                data-test='forecasts-offered-checkbox'
                            >
                                <label>
                                    <input
                                        type='checkbox'
                                        defaultChecked={marketModel.forecastsOffered}
                                        onChange={state.onForecastOfferedChange}
                                    />
                                    <span>Forecasts offered</span>
                                </label>
                            </div>
                        </div>
                        <div className='col-6'>
                            <div
                                className='element checkbox'
                                data-test='tricasts-offered-checkbox'
                            >
                                <label>
                                    <input
                                        type='checkbox'
                                        defaultChecked={marketModel.tricastsOffered}
                                        onChange={state.onTricastOfferedChange}
                                    />
                                    <span>Tricasts offered</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
            <MarketEachWay marketId={marketModel.id2} />
        </div>
    );
});
